import React, { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { MemoryRouter, useInRouterContext } from "react-router-dom";
import { useStore } from "@rapid/store";
import { preloadFFmpeg } from "@rapid/utils/videoEditing";
import { FlowProvider } from "@rapid/contexts/FlowProvider";
import { AuthProvider } from "@rapid/contexts/AuthProvider";
import { DeployContextProvider } from "@rapid/contexts/DeployProvider";
import { TutorialProvider } from "@rapid/contexts/TutorialProvider";
import { AnalyticsProvider } from "@rapid/contexts/AnalyticsProvider";
import { FpsProvider } from "@rapid/contexts/FpsProvider";
import { UpgradeModalProvider } from "@rapid/contexts/UpgradeModalProvider";
import { UrlModalProvider } from "@rapid/contexts/UrlModalProvider";
import { WorkspaceProvider } from "@rapid/contexts/WorkspaceProvider";
import { WorkflowProvider } from "@rapid/contexts/WorkflowProvider";
import { ProjectSelectorProvider } from "@rapid/contexts/ProjectSelectorProvider";
import { RapidExperimentProvider, RapidActivitiesProvider } from "./RapidAppProviders";
import { EmbeddedAuthBridge } from "./auth/EmbeddedAuthBridge";
import { EmbeddedProvider, RapidEmbedSource } from "./EmbeddedContext";
import { DatabaseProvider } from "./database/DatabaseProvider";
import { createModularFirestoreAdapter } from "./database/ModularFirestoreAdapter";
import type { DatabaseAdapter } from "./database/DatabaseAdapter";
import type { AuthAdapter } from "./auth/AuthAdapter";

export interface RapidProviderStackProps {
    queryClient: QueryClient;
    children: React.ReactNode;
    embedded?: boolean;
    embedSource?: RapidEmbedSource;
    databaseAdapter?: DatabaseAdapter;
    authAdapter?: AuthAdapter;
    workspaceId?: string;
    workflowId?: string;
}

function RouterFallback({ children }: { children: React.ReactNode }) {
    const inRouter = useInRouterContext();
    if (inRouter) return <>{children}</>;
    return <MemoryRouter>{children}</MemoryRouter>;
}

const defaultModularAdapter = createModularFirestoreAdapter();

export function RapidProviderStack({
    children,
    embedded,
    embedSource,
    databaseAdapter,
    authAdapter
}: RapidProviderStackProps) {
    const isEmbedded = embedded || !!embedSource;
    const dbAdapter = databaseAdapter ?? defaultModularAdapter;

    useEffect(() => {
        useStore.setState({ _database: dbAdapter });
    }, [dbAdapter]);

    useEffect(() => {
        preloadFFmpeg().catch(() => {});
    }, []);

    const innerProviders = (
        <WorkspaceProvider>
            <WorkflowProvider>
                <RapidExperimentProvider>
                    <RapidActivitiesProvider>
                        <AnalyticsProvider>
                            <UpgradeModalProvider>
                                <FpsProvider>
                                    <FlowProvider>
                                        <DeployContextProvider>
                                            <TutorialProvider>
                                                <UrlModalProvider>{children}</UrlModalProvider>
                                            </TutorialProvider>
                                        </DeployContextProvider>
                                    </FlowProvider>
                                </FpsProvider>
                            </UpgradeModalProvider>
                        </AnalyticsProvider>
                    </RapidActivitiesProvider>
                </RapidExperimentProvider>
            </WorkflowProvider>
        </WorkspaceProvider>
    );

    return (
        <EmbeddedProvider source={isEmbedded ? embedSource || RapidEmbedSource.App : undefined}>
            <DatabaseProvider adapter={dbAdapter}>
                <ProjectSelectorProvider>
                    <RouterFallback>
                        {isEmbedded ? (
                            <EmbeddedAuthBridge adapter={authAdapter}>
                                {innerProviders}
                            </EmbeddedAuthBridge>
                        ) : (
                            <AuthProvider>{innerProviders}</AuthProvider>
                        )}
                    </RouterFallback>
                </ProjectSelectorProvider>
            </DatabaseProvider>
        </EmbeddedProvider>
    );
}
